import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Card } from '../ui/card'
import { useToast } from '../../hooks/use-toast'
import { getThemeClass } from '../../lib/theme'
import { Alert, AlertDescription } from '../ui/alert'
import { supabase } from '../../lib/supabase'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../lib/auth.context'
import Background from '../background'
import StepsIndicator from '../steps-indicator'
import { InputOTP } from '../input-otp'
import { EnrollmentSuccess } from './totp-enrollment-success'

export function TOTPEnrollment() {
  const [qrCode, setQrCode] = useState<string>('')
  const [secret, setSecret] = useState<string>('')
  const [verifyCode, setVerifyCode] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [factorId, setFactorId] = useState<string>('')
  const { toast } = useToast()
  const { t } = useTranslation()
  const [showSuccess, setShowSuccess] = useState(false)

  useEffect(() => {
    const setupTOTP = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser()
        if (!user?.email) throw new Error('User email not found')

        const { data, error } = await supabase.auth.mfa.enroll({
          factorType: 'totp',
          issuer: 'FanFair Partner',
          friendlyName: `${t('title')} - ${user.email}`,
        })

        if (error) throw error

        if (data.totp) {
          setQrCode(data.totp.qr_code)
          setSecret(data.totp.secret)
          setFactorId(data.id)
        }
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to setup TOTP'
        setFormError(errorMessage)
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        })
      }
    }

    setupTOTP()
  }, [toast, t])

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    setIsLoading(true)

    try {
      const { data: challenge, error } = await supabase.auth.mfa.challenge({
        factorId: factorId,
      })

      if (error) throw error

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factorId,
        challengeId: challenge.id,
        code: verifyCode,
      })

      if (verifyError) throw verifyError

      toast({
        title: 'Success',
        description: 'TOTP device enrolled successfully',
      })

      // Refresh the session to ensure we have the latest token
      const {
        data: { session: newSession },
        error: sessionError,
      } = await supabase.auth.getSession()
      if (sessionError) throw sessionError

      // Ensure we have a valid session before redirecting
      if (!newSession?.access_token) {
        throw new Error('Failed to initialize session after TOTP enrollment')
      }

      setShowSuccess(true)
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to verify TOTP'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="w-full min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
        <div className="flex min-h-[500px]">
          {/* Left Panel - Marketing Content */}
          <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden rounded-l-2xl">
            <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(/fanfairpartnerlogin.jpg)` }}/>
            {/* Header */}
            <div className="absolute top-6 left-6 right-6 flex items-center justify-between z-10">
              <div className="flex items-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8"/>
              </div>
            </div>
            {/* Content */}
            {/*<div className="relative z-10 w-full flex items-center justify-center p-12">
              <div className="text-center text-white max-w-md">
                <h1 className="text-4xl font-bold mb-4 text-white">
                  Setup 2FA
                </h1>
                <p className="text-lg opacity-90 mb-8 text-white">
                  You need an authentication app to scan the QR below
                </p>
                <div className="flex justify-center space-x-2">
                  <div className="w-8 h-1 bg-white rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                </div>
              </div>
            </div>*/}
            <div className="relative z-10 w-full flex items-center justify-center p-12">
              <div className="text-center text-white max-w-md bg-black/60 p-10 border border-4 border-[#868686]/10 rounded-3xl">
                <h1 className="text-3xl font-bold mb-4 text-white">
                  Setup 2FA
                </h1>
                <p className="text-sm opacity-90 mb-8 text-white">
                  <p className="mb-2">Use the Google or Microsoft Authenticator app to scan the QR code with your phone.</p>
                  <p>Don’t have an authenticator app? Download one from your phone’s app store.</p>
                </p>
                {/* Slide indicators */}
                <div className="flex justify-center space-x-2 gap-8">
                  <img src="/GoogleAuthenticator.png" alt="Google Authenticator" className="w-15 h-15"/>
                  <img src="/MicrosoftAuthenticator.png" alt="Microsoft Authenticator" className="w-15 h-15"/>
                </div>
                {/*<div className="flex justify-center space-x-2">
                  <div className="w-8 h-1 bg-white rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                </div>*/}
              </div>
            </div>
          </div>
          {/* Right Panel - Login Form */}
          <div className="w-full lg:w-1/2 bg-slate-800 flex items-center justify-center p-6 lg:p-12">
            <div className="w-full max-w-sm space-y-8">
              {/* Mobile Header */}
              <div className="lg:hidden text-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8 mx-auto"/>
              </div>
              <div className="!mt-0">
                {!showSuccess ? (
                  <form onSubmit={handleVerify}>
                    <div className="space-y-2">
                      {/*<h1 className="text-2xl font-medium text-white">You need an authentication app to scan the QR below</h1>*/}
                      {/*<p className="text-slate-400 font-light text-center mb-3">
                        (Google Authenticator, Microsoft Authenticator, etc).
                      </p>*/}
                    </div>
                    <div className="flex flex-col w-full gap-4 items-center">
                      <StepsIndicator currentStep={2} />
                      {formError && (
                        <Alert variant="destructive" className="mt-10">
                          <AlertDescription>{formError}</AlertDescription>
                        </Alert>
                      )}
                      <div className="flex flex-col mt-7 items-center gap-8 bg-white p-1 rounded-xl h-60 w-60">
                        {qrCode && <img src={qrCode} alt="QR Code" className="w-full h-full" />}
                      </div>
                      <p className="text-white font-normal leading-relaxed text-sm text-center">
                        <div className="text-primary font-semibold">Step 1</div>
                        Open authenticator app on you phone
                      </p>
                      <p className="text-white font-normal leading-relaxed text-sm text-center">
                        <div className="text-primary font-semibold">Step 2</div>
                        Scan the QR code
                      </p>
                      <p className="text-white font-normal leading-relaxed text-sm text-center">
                        <div className="text-primary font-semibold">Step 3</div>
                        Enter 6-digit generated by the app below
                      </p>
                      <InputOTP length={6} onChange={(value) => setVerifyCode(value)} />
                      <Button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                      >
                        Enable 2FA
                      </Button>
                    </div>
                  </form>
                ) : (
                  <EnrollmentSuccess />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
